import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueue } from "../contexts/QueueContext";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { useDocumentStore } from "../store/documentState";
import { useDealsStore } from "../store/dealsStore";
import { uploadFilesToDrive } from "../services/driveUploadService";
import { getEnv } from "../utils/env";
import {
  getProjektdokumentationTemplate,
  submitVotReport,
  updateProjektdokumentationStatus,
  upsertProjektdokumentation,
  VotReportEntry,
} from "../services/api";
import {
  ProjektfreigabeStatus,
  ProjektdokumentationChecklistItem,
  ProjektdokumentationFormData,
  ProjektdokumentationSection,
  ProjektdokumentationStoredRecord,
  ProjektdokumentationTemplate,
  ProjektdokumentationUploadTarget,
} from "../types/projektdokumentation";
import {
  getSectionProgress,
  isChecklistItemCompleted,
  isChecklistItemVisible,
  validateProjektdokumentation,
} from "./projektdokumentationValidation";

interface ProjektdokumentationWizardProps {
  projectId: string;
  phaseId: string;
  disabled?: boolean;
  onCompleted?: (completedAt: string) => void;
}

interface LocalDraftState {
  sections: ProjektdokumentationSection[];
  technicalQuestionsOpen: boolean;
  tightestAccessCm: string;
  heatingRoomAreaApprox: string;
  lowCeilingHeightNote: string;
  pvStatus: "no" | "existing" | "planned" | "";
  drainAvailable: "" | "yes" | "no";
  status: ProjektfreigabeStatus;
}

const statusLabels: Record<ProjektfreigabeStatus, string> = {
  draft: "Entwurf",
  in_review: "In Prüfung",
  ready: "Freigabefähig",
  approved: "Freigegeben",
};

const statusTone: Record<ProjektfreigabeStatus, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
  in_review: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  ready: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200",
};

const emptyDraftState = (): LocalDraftState => ({
  sections: [],
  technicalQuestionsOpen: false,
  tightestAccessCm: "",
  heatingRoomAreaApprox: "",
  lowCeilingHeightNote: "",
  pvStatus: "",
  drainAvailable: "",
  status: "draft",
});

const toDraftState = (
  template: ProjektdokumentationTemplate
): LocalDraftState => {
  const record = template.existingRecord;
  return {
    sections: record?.sections || template.sections,
    technicalQuestionsOpen: record?.technicalQuestionsOpen || false,
    tightestAccessCm:
      typeof record?.tightestAccessCm === "number" ? String(record.tightestAccessCm) : "",
    heatingRoomAreaApprox: record?.heatingRoomAreaApprox || "",
    lowCeilingHeightNote: record?.lowCeilingHeightNote || "",
    pvStatus: record?.pvStatus || "",
    drainAvailable:
      typeof record?.drainAvailable === "boolean"
        ? record.drainAvailable
          ? "yes"
          : "no"
        : "",
    status: record?.status || "draft",
  };
};

const buildPayload = (
  projectId: string,
  draft: LocalDraftState
): ProjektdokumentationFormData => ({
  // technicalQuestionsOpen wird primär aus dem Checklist-Item abgeleitet,
  // damit Anzeige- und Persistenzzustand nicht auseinanderlaufen.
  projectId,
  sections: draft.sections,
  technicalQuestionsOpen: (() => {
    const item = draft.sections
      .flatMap((section) => section.items)
      .find((entry) => entry.id === "technical-questions-status");
    if (typeof item?.valueBoolean === "boolean") {
      return !item.valueBoolean;
    }
    return draft.technicalQuestionsOpen;
  })(),
  tightestAccessCm: /^\d+$/.test(draft.tightestAccessCm) ? Number(draft.tightestAccessCm) : null,
  heatingRoomAreaApprox: draft.heatingRoomAreaApprox.trim() || undefined,
  lowCeilingHeightNote: draft.lowCeilingHeightNote.trim() || undefined,
  pvStatus: draft.pvStatus || null,
  drainAvailable:
    draft.drainAvailable === "yes" ? true : draft.drainAvailable === "no" ? false : null,
});

const formatItemValue = (item: ProjektdokumentationChecklistItem): string | null => {
  switch (item.inputType) {
    case "text":
      return item.valueString?.trim() || null;
    case "select": {
      const value = item.valueString?.trim();
      if (!value) return null;
      return item.options?.find((option) => option.value === value)?.label || value;
    }
    case "number":
      return typeof item.valueNumber === "number" && Number.isFinite(item.valueNumber)
        ? `${item.valueNumber}${item.inputLabel ? ` ${item.inputLabel}` : ""}`
        : null;
    case "boolean":
      return typeof item.valueBoolean === "boolean" ? (item.valueBoolean ? "Ja" : "Nein") : null;
    case "url":
      return item.valueUrl?.trim() || null;
    default:
      return null;
  }
};

const VOT_REPORT_SUPPRESSED_ITEM_IDS = new Set([
  // Der Wert ist identisch zu "tightest-access-recorded" und soll im Bericht nicht doppelt erscheinen.
  "tightest-access-confirmed",
]);

const VOT_REPORT_QUESTION_LABELS: Record<string, string> = {
  "tightest-access-recorded": "Engste Stelle Zuweg / Einbringung",
  "heating-room-area-recorded": "Ungefähre Fläche Heizraum",
  "drain-availability-recorded": "Abfluss im Heizraum / Keller",
  "low-ceiling-height-present": "Niedrige Deckenhöhe vorhanden",
  "low-ceiling-height-documented": "Niedrige Deckenhöhe dokumentiert",
  "pv-status-recorded": "PV-Anlage vorhanden/geplant",
  "technical-questions-status": "Offene technische Fragen",
};

const VOT_REPORT_VALUE_UNITS: Record<string, string> = {
  "tightest-access-recorded": "cm",
  "tightest-access-confirmed": "cm",
  "heating-room-area-recorded": "m²",
};

const getVotReportQuestion = (item: ProjektdokumentationChecklistItem): string =>
  VOT_REPORT_QUESTION_LABELS[item.id] || item.label;

const valueIncludesUnit = (value: string, unit: string): boolean => {
  if (unit === "m²") {
    return /(?:^|[\d\s])(?:m²|m2|qm|quadratmeter)(?=$|[\s.,;)])/i.test(value);
  }

  return /(?:^|[\d\s])cm(?=$|[\s.,;)])/i.test(value);
};

const formatVotReportMeasuredValue = (
  item: ProjektdokumentationChecklistItem,
  value: string
): string => {
  const unit = VOT_REPORT_VALUE_UNITS[item.id];
  if (!unit || valueIncludesUnit(value, unit)) {
    return value;
  }

  return `${value} ${unit}`;
};

const formatVotBooleanValue = (item: ProjektdokumentationChecklistItem): string | null => {
  if (typeof item.valueBoolean !== "boolean") {
    return null;
  }

  if (item.id === "technical-questions-status") {
    return item.valueBoolean
      ? "Nein, keine offenen technischen Fragen"
      : "Ja, offene technische Fragen vorhanden";
  }

  return item.valueBoolean ? "Ja" : "Nein";
};

const formatVotReportValue = (item: ProjektdokumentationChecklistItem): string | null => {
  if (item.inputType === "boolean") {
    const value = formatVotBooleanValue(item);
    return value ? `Antwort: ${value}` : null;
  }

  if (item.inputType === "select") {
    const value = formatItemValue(item);
    return value ? `Antwort: ${value}` : null;
  }

  if (item.inputType === "url") {
    const value = formatItemValue(item);
    return value ? `Link: ${value}` : null;
  }

  const value = formatItemValue(item);
  return value ? `Wert: ${formatVotReportMeasuredValue(item, value)}` : null;
};

const buildVotReportEntries = (data: ProjektdokumentationFormData): VotReportEntry[] => {
  const entries: VotReportEntry[] = [];

  data.sections.forEach((section) => {
    section.items.forEach((item) => {
      if (
        VOT_REPORT_SUPPRESSED_ITEM_IDS.has(item.id) ||
        !isChecklistItemVisible(data, section.id, item.id)
      ) {
        return;
      }

      const parts: string[] = [];
      const value = formatVotReportValue(item);
      const comment = item.comment?.trim();
      if (value) parts.push(value);
      if (comment) parts.push(`Bemerkung: ${comment}`);

      if (parts.length === 0) {
        return;
      }

      entries.push({
        section: `[${section.code}] ${section.title}`,
        question: getVotReportQuestion(item),
        answer: parts.join("\n"),
      });
    });
  });

  if (data.lowCeilingHeightNote?.trim()) {
    entries.push({
      section: "[K] Technische Angaben",
      question: "Niedrige Deckenhöhe - Notiz",
      answer: `Bemerkung: ${data.lowCeilingHeightNote.trim()}`,
    });
  }

  return entries;
};

const base64ToFile = (base64Content: string, fileName: string, mimeType: string): File => {
  const binary = atob(base64Content);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new File([bytes], fileName, { type: mimeType });
};

const updateBoundFieldsForItem = (
  previous: LocalDraftState,
  itemId: string,
  item: ProjektdokumentationChecklistItem
): Partial<LocalDraftState> => {
  switch (itemId) {
    case "tightest-access-recorded":
    case "tightest-access-confirmed":
      return {
        tightestAccessCm:
          typeof item.valueNumber === "number" ? String(item.valueNumber) : previous.tightestAccessCm,
      };
    case "heating-room-area-recorded":
      return { heatingRoomAreaApprox: item.valueString || previous.heatingRoomAreaApprox };
    case "low-ceiling-height-recorded":
      return { lowCeilingHeightNote: item.valueString || previous.lowCeilingHeightNote };
    case "pv-status-recorded":
      return {
        pvStatus:
          item.valueString === "no" || item.valueString === "existing" || item.valueString === "planned"
            ? item.valueString
            : previous.pvStatus,
      };
    case "drain-availability-recorded":
      return {
        drainAvailable:
          typeof item.valueBoolean === "boolean"
            ? item.valueBoolean
              ? "yes"
              : "no"
            : previous.drainAvailable,
      };
    case "technical-questions-status":
      return {
        technicalQuestionsOpen:
          typeof item.valueBoolean === "boolean" ? !item.valueBoolean : previous.technicalQuestionsOpen,
      };
    default:
      return {};
  }
};

const ProjektdokumentationWizard: React.FC<ProjektdokumentationWizardProps> = ({
  projectId,
  phaseId,
  disabled = false,
  onCompleted,
}) => {
  const { addToQueue } = useQueue();
  const { showToast } = useToast();
  const { user } = useAuth();
  const updatePhaseStatus = useDocumentStore((state) => state.updatePhaseStatus);
  const upsertDeal = useDealsStore((state) => state.upsertDeal);
  const [template, setTemplate] = useState<ProjektdokumentationTemplate | null>(null);
  const [draft, setDraft] = useState<LocalDraftState>(emptyDraftState);
  const [storedRecord, setStoredRecord] = useState<ProjektdokumentationStoredRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const autosaveRef = useRef<number | null>(null);

  const draftStorageKey = useMemo(
    () => `projektdokumentation-draft:${projectId}:${phaseId}`,
    [phaseId, projectId]
  );

  const payload = useMemo(() => buildPayload(projectId, draft), [draft, projectId]);
  const validation = useMemo(() => validateProjektdokumentation(payload), [payload]);
  const currentStatus = storedRecord?.status || draft.status;
  const isApproved = currentStatus === "approved";
  const isReadOnly = disabled || isApproved;
  const statusAction =
    currentStatus === "draft"
      ? {
          label: "Freigabe anfragen",
          nextStatus: "ready" as const,
          className: "bg-blue-600 text-white",
          disabled: !validation.isReadyForApproval,
        }
      : currentStatus === "ready"
        ? {
            label: "Prüfung starten",
            nextStatus: "in_review" as const,
            className: "border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300",
            disabled: false,
          }
        : currentStatus === "in_review"
          ? {
              label: "Freigabe",
              nextStatus: "approved" as const,
              className: "bg-green-600 text-white",
              disabled: !validation.isReadyForApproval,
            }
          : null;
  const missingRequiredItems = useMemo(() => {
    const sourceItems = validation.missingRequiredItems;
    const uniqueItems = Array.from(
      new Set(
        sourceItems
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      )
    );

    return uniqueItems;
  }, [validation.missingRequiredItems]);
  const isDirectChoiceItem = (item: ProjektdokumentationChecklistItem) =>
    item.inputType === "boolean" || item.inputType === "select";
  const isVisibleInCurrentPayload = (sectionId: string, itemId: string) =>
    isChecklistItemVisible(payload, sectionId, itemId);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  };

  const getBulkCompletedItem = (item: ProjektdokumentationChecklistItem) => {
    if (item.id === "low-ceiling-height-present") {
      return {
        ...item,
        checked: false,
        valueBoolean: null,
      };
    }

    if (item.inputType === "boolean") {
      return {
        ...item,
        checked: true,
        valueBoolean: typeof item.valueBoolean === "boolean" ? item.valueBoolean : true,
      };
    }

    if (item.inputType === "select") {
      return {
        ...item,
        checked: true,
        valueString: item.valueString || item.options?.[0]?.value || "",
      };
    }

    return {
      ...item,
      checked: true,
    };
  };

  const getBulkClearedItem = (item: ProjektdokumentationChecklistItem) => {
    if (item.inputType === "boolean") {
      return {
        ...item,
        checked: false,
        valueBoolean: null,
      };
    }

    if (item.inputType === "select") {
      return {
        ...item,
        checked: false,
        valueString: "",
      };
    }

    return {
      ...item,
      checked: false,
    };
  };

  const setSectionCompleted = (sectionId: string, checked: boolean) => {
    if (isReadOnly) {
      return;
    }

    setDraft((previous) => {
      let nextTechnicalQuestionsOpen = previous.technicalQuestionsOpen;
      let nextDrainAvailable = previous.drainAvailable;
      let nextPvStatus = previous.pvStatus;
      const previousPayload = buildPayload(projectId, previous);

      const sections = previous.sections.map((section) => {
        if (section.id !== sectionId) {
          return section;
        }

        const visibleItemIds = new Set(
          section.items
            .filter((item) => isChecklistItemVisible(previousPayload, section.id, item.id))
            .map((item) => item.id)
        );

        return {
          ...section,
          items: section.items.map((item) => {
            if (!visibleItemIds.has(item.id)) {
              return item;
            }

            const nextItem = checked ? getBulkCompletedItem(item) : getBulkClearedItem(item);

            if (item.id === "technical-questions-status") {
              nextTechnicalQuestionsOpen =
                typeof nextItem.valueBoolean === "boolean"
                  ? !nextItem.valueBoolean
                  : previous.technicalQuestionsOpen;
            }

            if (item.id === "drain-availability-recorded") {
              nextDrainAvailable =
                typeof nextItem.valueBoolean === "boolean"
                  ? nextItem.valueBoolean
                    ? "yes"
                    : "no"
                  : "";
            }

            if (item.id === "pv-status-recorded") {
              nextPvStatus =
                nextItem.valueString === "no" ||
                nextItem.valueString === "existing" ||
                nextItem.valueString === "planned"
                  ? nextItem.valueString
                  : "";
            }

            return nextItem;
          }),
        };
      });

      return {
        ...previous,
        sections,
        technicalQuestionsOpen: nextTechnicalQuestionsOpen,
        drainAvailable: nextDrainAvailable,
        pvStatus: nextPvStatus,
      };
    });
  };

  const openResolvedFolder = async (target: ProjektdokumentationUploadTarget) => {
    if (target.url) {
      window.open(target.url, "_blank", "noopener,noreferrer");
    }
  };

  const renderUploadTarget = (target: ProjektdokumentationUploadTarget) => (
    <div key={target.label} className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {target.label}
      </p>
      {target.url ? (
        <button
          type="button"
          onClick={() => void openResolvedFolder(target)}
          className="mt-2 inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-300"
        >
          Ordner öffnen
        </button>
      ) : (
        <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          Uploads laufen über die bestehende Projekt-/Drive-Struktur
        </p>
      )}
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 break-words">
        {target.pathLabel}
      </p>
    </div>
  );

  const syncDealProjectStatus = (projectStatus: any, record?: ProjektdokumentationStoredRecord | null) => {
    const existingDeal = useDealsStore.getState().deals.find((deal) => deal.id === projectId);
    if (!existingDeal) {
      return;
    }

    const existingSnapshot = existingDeal.project_status || null;
    const nextSnapshot = projectStatus
      ? {
          ...(existingSnapshot || {}),
          ...projectStatus,
        }
      : existingSnapshot
        ? { ...existingSnapshot }
        : null;

    if (nextSnapshot && record?.status) {
      nextSnapshot.projektdokumentationApprovalStatus = record.status;
    }

    upsertDeal({
      ...existingDeal,
      project_status: nextSnapshot,
    });
  };

  useEffect(() => {
    let mounted = true;

    const loadTemplate = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getProjektdokumentationTemplate(projectId);
        if (!mounted) {
          return;
        }

        setTemplate(response);
        setStoredRecord(response.existingRecord);
        setExpandedSections(
          Object.fromEntries(response.sections.map((section) => [section.id, true]))
        );

        const fromServer = toDraftState(response);
        const rawDraft = window.localStorage.getItem(draftStorageKey);
        if (rawDraft && !response.existingRecord?.approvedAt) {
          try {
            const parsed = JSON.parse(rawDraft) as LocalDraftState;
            setDraft({
              ...fromServer,
              ...parsed,
            });
          } catch {
            setDraft(fromServer);
          }
        } else {
          setDraft(fromServer);
        }

        if (response.existingRecord?.status === "approved") {
          const approvedAt = response.existingRecord.approvedAt || response.existingRecord.updatedAt;
          updatePhaseStatus(
            phaseId,
            "completed",
            approvedAt || undefined,
            response.existingRecord.completedByEmail || undefined
          );
          onCompleted?.(approvedAt || response.existingRecord.updatedAt);
        } else {
          updatePhaseStatus(phaseId, "pending");
        }

        setHydrated(true);
      } catch (loadError) {
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Projektdokumentation konnte nicht geladen werden"
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTemplate();
    return () => {
      mounted = false;
    };
  }, [draftStorageKey, phaseId, projectId, updatePhaseStatus]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    if (isApproved) {
      window.localStorage.removeItem(draftStorageKey);
      return;
    }

    window.localStorage.setItem(draftStorageKey, JSON.stringify(draft));
  }, [draft, draftStorageKey, hydrated, isApproved]);

  useEffect(() => {
    if (!template || !hydrated || isReadOnly) {
      return;
    }

    if (autosaveRef.current) {
      window.clearTimeout(autosaveRef.current);
    }

    autosaveRef.current = window.setTimeout(() => {
      const save = async () => {
        try {
          if (navigator.onLine) {
            const response = await upsertProjektdokumentation(projectId, payload);
            setStoredRecord(response.record || null);
            syncDealProjectStatus(response.projectStatus, response.record || null);
            if (response.record?.status && response.record.status !== draft.status) {
              setDraft((previous) => ({
                ...previous,
                status: response.record?.status || previous.status,
              }));
            }
          } else {
            await addToQueue("projektdokumentation_upsert", {
              projectId,
              payload,
            });
          }
        } catch {
          await addToQueue("projektdokumentation_upsert", {
            projectId,
            payload,
          });
        }
      };

      void save();
    }, 750);

    return () => {
      if (autosaveRef.current) {
        window.clearTimeout(autosaveRef.current);
      }
    };
  }, [addToQueue, hydrated, isReadOnly, payload, projectId, template]);

  const setItem = (
    sectionId: string,
    itemId: string,
    updater: (item: ProjektdokumentationChecklistItem) => ProjektdokumentationChecklistItem
  ) => {
    if (isReadOnly) {
      return;
    }

    setDraft((previous) => {
      let nextItemState: ProjektdokumentationChecklistItem | null = null;
      const sections = previous.sections.map((section) => {
        if (section.id !== sectionId) {
          return section;
        }

        const nextSection = {
          ...section,
          items: section.items.map((item) => {
            if (item.id !== itemId) {
              return item;
            }

            nextItemState = updater(item);
            return nextItemState;
          }),
        };

        if (itemId === "drain-availability-recorded" && nextItemState?.valueBoolean === false) {
          nextSection.items = nextSection.items.map((item) =>
            item.id === "drain-documented"
              ? { ...item, checked: false, comment: undefined }
              : item
          );
        }

        if (itemId === "special-challenges-documented" && nextItemState?.checked === false) {
          nextSection.items = nextSection.items.map((item) =>
            item.id === "special-challenges-structural" ||
            item.id === "special-challenges-technical" ||
            item.id === "special-challenges-organizational"
              ? { ...item, checked: false, comment: undefined }
              : item
          );
        }

        return nextSection;
      });

      return {
        ...previous,
        sections,
        ...updateBoundFieldsForItem(previous, itemId, nextItemState || ({} as ProjektdokumentationChecklistItem)),
      };
    });
  };

  const setStatus = async (status: ProjektfreigabeStatus) => {
    if (isReadOnly || isSavingStatus) {
      return;
    }

    setIsSavingStatus(true);

    const optimisticApprovedAt = new Date().toISOString();

    const applyLocalStatus = (record?: ProjektdokumentationStoredRecord | null) => {
      if (record) {
        setStoredRecord(record);
        setDraft((previous) => ({
          ...previous,
          status: record.status,
          sections: record.sections,
        }));
      } else {
        setDraft((previous) => ({ ...previous, status }));
      }
    };

    try {
      if (navigator.onLine) {
        const submittedAt = new Date().toISOString();
        let votReportLink: string | null = null;

        if (status === "ready") {
          await upsertProjektdokumentation(projectId, payload);
          const report = await submitVotReport(projectId, {
            projectId,
            submittedAt,
            submittedByEmail: user?.email,
            entries: buildVotReportEntries(payload),
          });
          const pdfFile = base64ToFile(
            report.file.base64Content,
            report.file.fileName,
            report.file.mimeType
          );
          const uploadResult = await uploadFilesToDrive(
            [pdfFile],
            report.dealName || projectId,
            report.targetDirectories,
            getEnv("VITE_GOOGLE_SHARED_DRIVE_NAME") || undefined
          );
          const uploadedFile = uploadResult.files?.[0];
          if (!uploadedFile?.webViewLink) {
            throw new Error("VOT-PDF wurde erzeugt, aber nicht in Google Drive gespeichert");
          }
          votReportLink = uploadedFile.webViewLink;
        }

        const response = await updateProjektdokumentationStatus(projectId, {
          projectId,
          status,
        });
        applyLocalStatus(response.record || null);
        syncDealProjectStatus(response.projectStatus, response.record || null);

        if (status === "approved") {
          const approvedAt = response.record?.approvedAt || optimisticApprovedAt;
          updatePhaseStatus(
            phaseId,
            "completed",
            approvedAt,
            response.record?.completedByEmail || undefined
          );
          onCompleted?.(approvedAt);
          showToast("success", "Projektdokumentation freigegeben", 3000);
        } else if (status === "ready") {
          showToast(
            "success",
            votReportLink
              ? "Freigabe eingereicht und VOT-PDF in Google Drive gespeichert"
              : "Freigabe eingereicht",
            3000
          );
        } else {
          showToast("success", `Status aktualisiert: ${statusLabels[status]}`, 2500);
        }
      } else {
        applyLocalStatus();
        await addToQueue("projektdokumentation_status_update", {
          projectId,
          phaseId,
          payload: {
            projectId,
            status,
          },
        });

        if (status === "approved") {
          updatePhaseStatus(phaseId, "completed", optimisticApprovedAt);
          onCompleted?.(optimisticApprovedAt);
        }
        showToast("info", "Status wurde offline in die Queue gelegt", 3000);
      }
    } catch (statusError) {
      showToast(
        "error",
        statusError instanceof Error ? statusError.message : "Status konnte nicht aktualisiert werden",
        4000
      );
    } finally {
      setIsSavingStatus(false);
    }
  };

  const renderItemInput = (sectionId: string, item: ProjektdokumentationChecklistItem) => {
    if (!item.inputType || item.inputType === "none") {
      return null;
    }

    const commonClassName =
      "mt-3 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white";

    if (item.inputType === "text" || item.inputType === "url") {
      const value = item.inputType === "url" ? item.valueUrl || "" : item.valueString || "";
      return (
        <input
          type={item.inputType === "url" ? "url" : "text"}
          value={value}
          disabled={isReadOnly}
          onChange={(event) =>
            setItem(sectionId, item.id, (current) => ({
              ...current,
              [item.inputType === "url" ? "valueUrl" : "valueString"]: event.target.value,
              checked: current.checked || event.target.value.trim().length > 0,
            }))
          }
          className={commonClassName}
          placeholder={item.inputPlaceholder || item.inputLabel || "Eintragen"}
        />
      );
    }

    if (item.inputType === "number") {
      return (
        <input
          type="text"
          inputMode="numeric"
          value={typeof item.valueNumber === "number" ? String(item.valueNumber) : ""}
          disabled={isReadOnly}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw !== "" && !/^\d+$/.test(raw)) {
              return;
            }

            setItem(sectionId, item.id, (current) => ({
              ...current,
              valueNumber: raw === "" ? null : Number(raw),
              checked: current.checked || raw !== "",
            }));
          }}
          className={commonClassName}
          placeholder={item.inputPlaceholder || "Zahl eingeben"}
        />
      );
    }

    if (item.inputType === "boolean") {
      const selectedValue =
        typeof item.valueBoolean === "boolean" ? (item.valueBoolean ? "yes" : "no") : "";
      const booleanOptions =
        item.id === "technical-questions-status"
          ? [
              { value: true, label: "Ja (keine offen)" },
              { value: false, label: "Nein (es gibt offene)" },
            ]
          : [
              { value: true, label: "Ja" },
              { value: false, label: "Nein" },
            ];
      return (
        <div className="mt-3 grid grid-cols-2 gap-3">
          {booleanOptions.map((option) => {
            const isSelected =
              typeof item.valueBoolean === "boolean" && item.valueBoolean === option.value;

            return (
              <button
                key={option.label}
                type="button"
                disabled={isReadOnly}
                onClick={() =>
                  setItem(sectionId, item.id, (current) => ({
                    ...current,
                    valueBoolean: option.value,
                    checked: true,
                  }))
                }
                className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                  isSelected
                    ? "border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200"
                    : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                }`}
                aria-pressed={selectedValue === (option.value ? "yes" : "no")}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }

    if (item.inputType === "select") {
      return (
        <select
          value={item.valueString || ""}
          disabled={isReadOnly}
          onChange={(event) =>
            setItem(sectionId, item.id, (current) => ({
              ...current,
              valueString: event.target.value,
              checked: event.target.value !== "",
            }))
          }
          className={commonClassName}
        >
          {!item.valueString && <option value="">Bitte wählen</option>}
          {(item.options || []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        {error || "Projektdokumentation konnte nicht geladen werden"}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow overflow-hidden">
        <div className="p-5 border-b dark:border-gray-700 bg-gradient-to-br from-slate-50 to-white dark:from-gray-800 dark:to-gray-800">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-[18rem]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-300">
                Sales → OPS
              </p>
              <h2 className="mt-2 text-xl font-semibold leading-7 text-gray-900 dark:text-white">
                Freigabe-Checkliste
              </h2>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Unterlagen so vollständig erfassen, dass Planung, Bestellung und Umsetzung direkt
                starten können.
              </p>
            </div>
            <div className="flex flex-col items-start gap-2 sm:items-end">
              <span className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${statusTone[currentStatus]}`}>
                {statusLabels[currentStatus]}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {validation.missingRequiredItems.length === 0
                  ? "Alle Pflichtpunkte erfüllt"
                  : `${validation.missingRequiredItems.length} Pflichtpunkte offen`}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Fortschritt
            </p>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {storedRecord?.completenessPercent ?? validation.completenessPercent}%
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Sichtbar
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {currentStatus === "approved"
                ? "Freigegeben"
                : validation.isReadyForApproval
                  ? "Prüfbar"
                  : "Unvollständig"}
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3 col-span-2">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Letzte Änderung
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
              {storedRecord?.updatedAt
                ? new Date(storedRecord.updatedAt).toLocaleString("de-DE")
                : "Noch nicht gespeichert"}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Upload-Ziel
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Anzeige aus der bestehenden HubSpot-/Drive-Infrastruktur. Diese Maske pflegt die Links nicht selbst.
            </p>
          </div>
          <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-900 dark:text-gray-300">
            {template.uploadTargetInfo.source === "hubspot" ? "Quelle: HubSpot" : "Quelle: bestehende Drive-Struktur"}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4">
          {renderUploadTarget(template.uploadTargetInfo.projectFolder)}
        </div>
      </div>

      {draft.sections.map((section) => {
        const progress = getSectionProgress(payload, section);
        const isExpanded = !!expandedSections[section.id];
        const visibleItems = section.items.filter((item) =>
          isVisibleInCurrentPayload(section.id, item.id)
        );
        const isSectionChecked =
          visibleItems.length > 0 && visibleItems.every((item) => item.checked);
        return (
          <div key={section.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className="flex w-full items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-left dark:border-gray-700 dark:bg-gray-900/40"
            >
              <span className="min-w-0">
                <span className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {section.code}
                </span>
                <span className="mt-1 block text-base font-semibold text-gray-900 dark:text-white">
                  {section.title}
                </span>
                {section.description && (
                  <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">
                    {section.description}
                  </span>
                )}
              </span>
              <span
                className={`text-gray-500 transition-transform dark:text-gray-400 ${
                  isExpanded ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            <div className="px-4 py-3 border-b dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={isSectionChecked}
                  disabled={isReadOnly}
                  onChange={(event) => setSectionCompleted(section.id, event.target.checked)}
                  className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {section.bulkToggleLabel || "Alle Unterpunkte"}
              </label>
              <div className="text-right">
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                    progress.isComplete
                      ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                  }`}
                >
                  {progress.isComplete ? "vollständig" : "unvollständig"}
                </span>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {progress.completed} / {progress.total}
                </p>
              </div>
            </div>

            {isExpanded && (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {visibleItems.map((item) => {
                  const completed = isChecklistItemCompleted(item);
                  return (
                    <div key={item.id} className="p-4">
                      {isDirectChoiceItem(item) ? (
                        <div>
                          <span className="block text-sm font-medium text-gray-900 dark:text-white">
                            {item.label} {item.required ? "*" : ""}
                          </span>
                          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                            {completed ? "Erfüllt" : item.required ? "Pflichtpunkt" : "Optional"}
                          </span>
                        </div>
                      ) : (
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.checked}
                            disabled={isReadOnly}
                            onChange={(event) =>
                              setItem(section.id, item.id, (current) => ({
                                ...current,
                                checked: event.target.checked,
                              }))
                            }
                            className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-900 dark:text-white">
                              {item.label} {item.required ? "*" : ""}
                            </span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                              {completed ? "Erfüllt" : item.required ? "Pflichtpunkt" : "Optional"}
                            </span>
                          </span>
                        </label>
                      )}

                      {renderItemInput(section.id, item)}

                      <textarea
                        value={item.comment || ""}
                        disabled={isReadOnly}
                        onChange={(event) =>
                          setItem(section.id, item.id, (current) => ({
                            ...current,
                            comment: event.target.value,
                          }))
                        }
                        rows={2}
                        className="mt-3 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white"
                        placeholder="Bemerkung optional"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Vollständigkeit & Freigabe
        </h3>

        {missingRequiredItems.length > 0 ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <p className="font-medium">Fehlende Pflichtpunkte</p>
            <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
              {missingRequiredItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
            <p className="font-medium">Alle Pflichtpunkte sind erfüllt.</p>
          </div>
        )}

        {storedRecord?.completedAt && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
            <p>
              Abgeschlossen am {new Date(storedRecord.completedAt).toLocaleString("de-DE")}
              {storedRecord.completedByEmail ? ` von ${storedRecord.completedByEmail}` : ""}.
            </p>
            {storedRecord.approvedAt && (
              <p className="mt-1">
                Freigegeben am {new Date(storedRecord.approvedAt).toLocaleString("de-DE")}
                {storedRecord.approvedByEmail ? ` von ${storedRecord.approvedByEmail}` : ""}.
              </p>
            )}
          </div>
        )}

        {statusAction && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setStatus(statusAction.nextStatus)}
              disabled={isReadOnly || isSavingStatus || statusAction.disabled}
              className={`min-h-[48px] rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${statusAction.className}`}
            >
              {isSavingStatus && statusAction.nextStatus === "ready"
                ? "Freigabe wird eingereicht..."
                : statusAction.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjektdokumentationWizard;
